import React, { useEffect, useState } from 'react';
import { Table, Button, Select, Row, Col, Tag, Space, message } from 'antd';
import {
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { showError } from '@components/notification';
import { formatCurrency, isNullOrEmpty } from '@utils/utils';
import { apiGetProcessManagement } from '@services/ManageCustomers/CustomerServices';
import { render } from '@testing-library/react';

const { Option } = Select;

const ProcessManagementList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const previousMonthDate = moment().subtract(1, 'month');
  const [selectedMonth, setSelectedMonth] = useState(previousMonthDate.format('YYYY-MM'));
  const [selectedYear, setSelectedYear] = useState(previousMonthDate.year());

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiGetProcessManagement({
        page: currentPage,
        size: pageSize,
        period: selectedMonth ? `${selectedMonth}-01` : undefined
      });

      setData(
        response.data?.data?.map((item, index) => ({
          ...item,
          stt: (currentPage - 1) * pageSize + 1 + index
        }))
      );
      setTotalItems(response.data?.meta?.total);
    } catch (error) {
      setData([]);
      setTotalItems(0);
      showError('Lỗi khi tải danh sách yêu cầu tính toán');
      console.error('Error fetching process management data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, selectedMonth]);

  const generateYearOptions = () => {
    const years = [];
    const currentYear = moment().year();
    for (let y = currentYear; y >= currentYear - 1; y--) {
      years.push({ label: `Năm ${y}`, value: y });
    }
    return years;
  };

  const generateMonthOptions = () => {
    const months = [];
    const current = moment();
    const isCurrentYear = selectedYear === current.year();
    const startOfYear = moment(`${selectedYear}-01-01`);
    const endMonth = isCurrentYear ? current.month() + 1 : 11;

    for (let m = 0; m <= endMonth; m++) {
      const monthMoment = startOfYear.clone().add(m, 'months');
      months.push({
        label: `Tháng cước ${monthMoment.format('MM/YYYY')}`,
        value: monthMoment.format('YYYY-MM')
      });
    }
    return months;
  };

  const handleYearChange = (value) => {
    setSelectedYear(value);
    setSelectedMonth(null);
  };

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
  };

  const handleChangePage = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const getStatusTag = (status) => {
    switch (status) {
      case 0:
        return (
          <Tag icon={<SyncOutlined spin />} color="processing">
            Đang chạy
          </Tag>
        );
      case 1:
        return (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Hoàn thành
          </Tag>
        );
      case 2:
        return (
          <Tag icon={<CloseCircleOutlined />} color="error">
            Thất bại
          </Tag>
        );
      default:
        return <Tag>Không xác định</Tag>;
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center'
    },
    // {
    //   title: 'Tên quy trình',
    //   dataIndex: 'process_name',
    //   key: 'process_name',
    //   width: 200
    // },
    {
      title: 'Kỳ tính',
      dataIndex: 'period',
      render: (date) => (date ? moment(date).format('MM/YYYY') : '-'),
      key: 'period',
      width: 120,
      align: 'center'
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      align: 'center',
      render: (status) => getStatusTag(status)
    },
    {
      title: 'Thời gian bắt đầu',
      dataIndex: 'started_at',
      key: 'started_at',
      width: 180,
      render: (date) => (date ? moment(date).format('DD/MM/YYYY HH:mm:ss') : '-')
    },
    {
      title: 'Thời gian hoàn thành',
      dataIndex: 'completed_at',
      key: 'completed_at',
      width: 180,
      render: (date) => (date ? moment(date).format('DD/MM/YYYY HH:mm:ss') : '-')
    },
    {
      title: 'Người tạo',
      dataIndex: 'created_by',
      key: 'created_by',
      width: 150
    },
    {
      title: 'Lỗi',
      dataIndex: 'error_message',
      key: 'error_message',
      width: 200,
      render: (error) =>
        error ? (
          <span style={{ color: '#e11d48', fontSize: '12px' }} title={error}>
            {error.length > 50 ? `${error.substring(0, 50)}...` : error}
          </span>
        ) : (
          '-'
        )
    }
  ];

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col md={3} xs={6}>
          <Select
            style={{ width: '100%', height: '36px' }}
            placeholder="Chọn năm"
            value={selectedYear}
            onChange={handleYearChange}>
            {generateYearOptions().map((year) => (
              <Option key={year.value} value={year.value}>
                {year.label}
              </Option>
            ))}
          </Select>
        </Col>
        <Col md={5} xs={8}>
          <Select
            style={{ width: '100%', height: '36px' }}
            placeholder="Chọn tháng cước"
            value={selectedMonth}
            onChange={handleMonthChange}>
            {generateMonthOptions().map((month) => (
              <Option key={month.value} value={month.value}>
                {month.label}
              </Option>
            ))}
          </Select>
        </Col>
        <Col md={5} xs={6}>
          <Button
            type="primary"
            onClick={fetchData}
            loading={loading}
            style={{
              height: '36px',
              backgroundColor: '#0284c7',
              borderColor: '#0284c7'
              // fontSize: '18px'
            }}>
            Tìm kiếm
          </Button>
        </Col>
      </Row>

      <Table
        bordered
        loading={loading}
        dataSource={data}
        columns={columns}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalItems,
          showSizeChanger: true,
          onChange: handleChangePage,
          onShowSizeChange: handleChangePage,
          showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
        }}
        scroll={{ x: 1200 }}
      />
    </>
  );
};

export default ProcessManagementList;
